import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, fmtDateTime, EmptyState } from "./_shared";

const LIMIT = 50;

export default function AdminLogs() {
  const [action, setAction] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/logs", {
          params: { action: action || undefined, skip, limit: LIMIT },
        });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [action, skip]);

  const actions = Array.from(new Set(data.items.map((i) => i.action))).sort();

  return (
    <AdminLayout
      title="Audit log"
      subtitle="Every admin action — reruns, exports, status changes, notes — is recorded."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-logs-filters">
          <select
            value={action}
            onChange={(e) => {
              setSkip(0);
              setAction(e.target.value);
            }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            data-testid="admin-logs-action-select"
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Card>

        <Card className="overflow-hidden" testid="admin-logs-table-card">
          {loading ? (
            <div
              className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500"
              data-testid="admin-logs-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="No logs yet"
              body="Once admins act on data, entries will land here."
              testid="admin-logs-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">When</th>
                    <th className="text-left px-5 py-2.5 font-medium">Action</th>
                    <th className="text-left px-5 py-2.5 font-medium">Actor</th>
                    <th className="text-left px-5 py-2.5 font-medium">Target</th>
                    <th className="text-left px-5 py-2.5 font-medium">Resource</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="admin-logs-tbody">
                  {data.items.map((l) => (
                    <tr
                      key={l.log_id}
                      className="hover:bg-slate-50"
                      data-testid={`admin-log-${l.log_id}`}
                    >
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap">
                        {fmtDateTime(l.created_at)}
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-mono text-xs">{l.action}</span>
                      </td>
                      <td className="px-5 py-3 text-slate-700">{l.actor_email}</td>
                      <td className="px-5 py-3 text-slate-500 font-mono text-xs">
                        {l.target_user_id || "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-500 font-mono text-xs">
                        {l.resource || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-logs"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
